import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    CircularProgress,
    Divider,
    TablePagination,
    Chip
} from '@mui/material';
import { Close as CloseIcon, OpenInNew as OpenInNewIcon, Launch as LaunchIcon } from '@mui/icons-material';
import { ICliente } from '../../interface/ICliente';
import { IPedido } from '../../../pedidos/interface/IPedido';
import { StatusPedido, StatusPedidoDescricao, StatusPedidosCores } from '../../../pedidos/enum/StatusPedido';
import { TipoMarketplace, TipoMarketplaceCor } from '../../../marketplace/enum/TipoMarketplace';
import { useEffect, useState } from 'react';
import { usePedidos } from '../../../pedidos/hooks/usePedidos';
import { useNavigate } from 'react-router-dom';
import { useDataHora } from '../../../core/hooks/useDataHora';

interface DetalhesPedidosDrawerProps {
    cliente: ICliente | null;
    open: boolean;
    onClose: () => void;
}

export const DetalhesPedidosDrawer = ({ cliente, open, onClose }: DetalhesPedidosDrawerProps) => {
    const [pedidos, setPedidos] = useState<IPedido[]>([]);
    const [loading, setLoading] = useState(false);
    const [paginaAtual, setPaginaAtual] = useState(0);
    const [linhasPorPagina, setLinhasPorPagina] = useState(10);
    const { listarPedidos } = usePedidos();
    const navigate = useNavigate();
    const { dataHoraBr } = useDataHora();

    useEffect(() => {
        if (open && cliente?.uuid) {
            buscarPedidos();
        }
    }, [open, cliente]);

    const buscarPedidos = async () => {
        if (!cliente?.uuid) return;
        
        setLoading(true);
        try {
            const resultado = await listarPedidos({ clienteUuid: cliente.uuid });
            if (resultado) {
                setPedidos(resultado);
            }
        } catch (error) {
            console.error('Erro ao buscar pedidos:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatarValor = (valor: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(valor);
    };

    const getStatusText = (status: StatusPedido) => {
        return StatusPedido[status] as keyof typeof StatusPedidoDescricao;
    };

    const getStatusColor = (status: StatusPedido) => {
        const statusText = getStatusText(status);
        return StatusPedidosCores[statusText];
    };

    const getMarketplaceColor = (tipo: TipoMarketplace) => {
        return TipoMarketplaceCor[tipo];
    };

    const getMarketplaceLabel = (tipo: TipoMarketplace) => {
        switch (tipo) {
            case TipoMarketplace.PDV:
                return 'PDV';
            case TipoMarketplace.IFOOD:
                return 'iFood';
            case TipoMarketplace.WHATSAPP:
                return 'WhatsApp';
            case TipoMarketplace.CARDAPIO_DIGITAL_DELIVERY_RETIRADA:
                return 'Cardápio Digital';
            default:
                return tipo;
        }
    };

    const handlePaginaChange = (event: unknown, newPage: number) => {
        setPaginaAtual(newPage);
    };

    const handleLinhasPorPaginaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLinhasPorPagina(parseInt(event.target.value, 10));
        setPaginaAtual(0);
    };

    const abrirDetalhesPedido = (uuid: string) => {
        navigate(`/pedidos/${uuid}`);
        onClose();
    };

    const abrirDetalhesPedidoNovaAba = (uuid: string) => {
        window.open(`/pedidos/${uuid}`, '_blank');
    };

    const indiceFinal = (paginaAtual + 1) * linhasPorPagina;
    const indiceInicial = indiceFinal - linhasPorPagina;
    const pedidosPaginaAtual = pedidos?.slice(indiceInicial, indiceFinal);

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: { 
                    width: '900px',
                    zIndex: 99999
                }
            }}
            sx={{
                zIndex: 99999
            }}
        >
            <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                    <Box>
                        <Typography 
                            variant="h5"
                            fontSize={18} 
                        >
                            {cliente?.nome}
                        </Typography>
                        <Typography 
                            variant="subtitle1" 
                            fontSize={15} 
                            component="h2" 
                            sx={{ 
                                color: 'text.secondary'
                            }}
                        >
                            {pedidos.length} pedidos
                        </Typography>
                    </Box>
                    <IconButton 
                        onClick={onClose}
                        sx={{ 
                            color: 'text.secondary',
                            '&:hover': {
                                backgroundColor: 'action.hover'
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                
                <Divider sx={{ mb: 3 }} />
                
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box>
                        {pedidos.length > 0 ? (
                            <Box sx={{ overflow: 'auto' }}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Número</TableCell>
                                            <TableCell>Valor</TableCell>
                                            <TableCell>Itens</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Marketplace</TableCell>
                                            <TableCell>Data/Hora</TableCell>
                                            <TableCell align="center">Ações</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {pedidosPaginaAtual?.map((pedido) => (
                                            <TableRow key={pedido.uuid} hover>
                                                <TableCell>{pedido.numeroPedido || pedido.numeroPedidoDiario || '-'}</TableCell>
                                                <TableCell>{formatarValor(pedido.valorTotal)}</TableCell>
                                                <TableCell>{pedido.produtos?.length || 0}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={getStatusText(pedido.status as StatusPedido)}
                                                        size="small"
                                                        sx={{
                                                            color: getStatusColor(pedido.status as StatusPedido),
                                                            border: `1px solid ${getStatusColor(pedido.status as StatusPedido)}`,
                                                            backgroundColor: 'transparent'
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={getMarketplaceLabel(pedido.marketplace?.tipo || TipoMarketplace.PDV)}
                                                        size="small"
                                                        sx={{
                                                            color: 'white',
                                                            backgroundColor: getMarketplaceColor(pedido.marketplace?.tipo || TipoMarketplace.PDV)
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>{dataHoraBr(pedido.dataHora)}</TableCell>
                                                <TableCell align="center">
                                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => abrirDetalhesPedido(pedido.uuid)}
                                                            sx={{ color: 'primary.main' }}
                                                            title="Abrir detalhes do pedido"
                                                        >
                                                            <LaunchIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => abrirDetalhesPedidoNovaAba(pedido.uuid)}
                                                            sx={{ color: 'secondary.main' }}
                                                            title="Abrir detalhes em nova aba"
                                                        >
                                                            <OpenInNewIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                                <TablePagination
                                    component="div"
                                    count={pedidos.length}
                                    page={paginaAtual}
                                    onPageChange={handlePaginaChange}
                                    rowsPerPage={linhasPorPagina}
                                    onRowsPerPageChange={handleLinhasPorPaginaChange}
                                    rowsPerPageOptions={[5, 10, 25]}
                                    labelRowsPerPage="Linhas por página:"
                                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                                />
                            </Box>
                        ) : (
                            <Box sx={{ textAlign: 'center', p: 4 }}>
                                <Typography variant="body1" color="text.secondary">
                                    Nenhum pedido encontrado para este cliente
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </Drawer>
    );
};
